import * as React from 'react';
import { connect } from 'react-redux';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import CrossButton from 'components/buttons/cross-button/CrossButton';
import ImageUploader from 'components/ImageUploader';

import { __t, applyTranslations } from 'utils/language';
import { getCloudinaryImageUrl } from 'utils/cloudinary';

import {
  setMainImage,
  unsetMainImage
} from 'modules/campaigns/reducers/campaignAssets';

import type { CampaignAsset } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./i18n/campaign-main-image-upload.i18n.json'));

type UploadingAsset = {
  file: File
};

type OwnProps = {
  mainImage: CampaignAsset | undefined | null
};

type DispatchProps = {
  dispatchSetMainImage: (a: UploadingAsset) => Promise<any>,
  dispatchUnsetMainImage: (a: CampaignAsset) => Promise<any>
};

type Props = OwnProps & DispatchProps;

const MEDIA_TYPES = ['image/*'];

class CampaignMainImageUpload extends React.PureComponent<Props> {
  applyNewImage(file: File): void {
    const { dispatchSetMainImage } = this.props;
    const image: UploadingAsset = { file };

    dispatchSetMainImage(image);
  }

  handleDelete = (event: SyntheticEvent<HTMLElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    const { mainImage, dispatchUnsetMainImage } = this.props;

    if (mainImage) {
      dispatchUnsetMainImage(mainImage);
    }
  };

  handleUploadFile = (file: File) => {
    const isImage: boolean = file.type.indexOf('image') >= 0;

    if (!isImage) {
      return;
    }

    this.applyNewImage(file);
  };

  renderUploadNew(): React.ReactNode {
    return (
      <ImageUploader
        multiple={false}
        mediaTypes={MEDIA_TYPES.join(',')}
        onUpload={this.handleUploadFile}
      >
        <section className="campaign-main-image-upload campaign-main-image-upload--placeholder">
          <div className="campaign-main-image-upload__container">
            <SVGDynamicIcon
              name="plus-inside-circle-custom-bg"
              width={25}
              height={25}
              customColor="grey-light"
            />
          </div>
        </section>
      </ImageUploader>
    );
  }

  renderExistingImage(image: CampaignAsset): React.ReactNode {
    const mainImageUrl = getCloudinaryImageUrl({ url: image.url }, { width: 222, height: 222 });

    return (
      <section className="campaign-main-image-upload">
        <div
          style={{ backgroundImage: `url(${String(mainImageUrl)})` }}
          className="campaign-main-image-upload__image"
        >
          <div className="campaign-main-image-upload__cross">
            <CrossButton
              type="corner-closer"
              onClick={this.handleDelete}
            />
          </div>
          <div className="campaign-main-image-upload__footer">
            <ImageUploader
              multiple={false}
              mediaTypes={MEDIA_TYPES.join(',')}
              onUpload={this.handleUploadFile}
            >
              <span className="campaign-main-image-upload__image-upload">
                {__t('campaign_main_image_upload.field_main_image_button')}
              </span>
            </ImageUploader>
          </div>
        </div>
      </section>
    );
  }

  render(): React.ReactNode {
    const { mainImage } = this.props;

    return mainImage ?
      this.renderExistingImage(mainImage) :
      this.renderUploadNew();
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSetMainImage: (image: UploadingAsset) => dispatch(setMainImage(image)),
  dispatchUnsetMainImage: () => dispatch(unsetMainImage())
});

export default connect(null, mapDispatchToProps)(CampaignMainImageUpload);
